import { useState } from "react";
import { DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { PlayerAvatar } from "./PlayerAvatar";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";

const SCORES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function RatingRow({ pid, name, value, onChange }) {
  return (
    <div className="space-y-1.5" data-testid={`vote-row-${pid}`}>
      <div className="flex items-center gap-2">
        <PlayerAvatar id={pid} name={name} size={24} />
        <span className="text-sm font-semibold text-white">{name}</span>
        <span className="ml-auto font-mono font-black text-emerald-400">{value ?? "—"}</span>
      </div>
      <div className="grid grid-cols-10 gap-1">
        {SCORES.map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`vote-${pid}-${n}`}
            onClick={() => onChange(n)}
            className={`h-8 rounded-md font-mono text-xs font-bold transition-colors ${
              value === n ? "bg-emerald-500 text-slate-950" : "bg-slate-800 text-slate-300 hover:bg-slate-700"
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function VotesList({ match, entry, playersMap, isAdmin, onDeleted }) {
  const votes = entry?.votes || [];
  const del = async (voter) => {
    try {
      await api.delete(`/matches/${match.id}/votes/${voter}`);
      toast.success("Voto eliminato");
      onDeleted();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  if (!votes.length) return <p className="text-xs text-slate-500" data-testid="votes-empty">Nessun voto ancora.</p>;
  return (
    <div className="space-y-2" data-testid="votes-list">
      {votes.map((v) => (
        <div key={v.voter_id} className="ap-glass rounded-xl p-2.5 text-xs" data-testid={`vote-entry-${v.voter_id}`}>
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold text-white">{playersMap[v.voter_id]?.short_name} ha votato</span>
            {isAdmin && (
              <button data-testid={`delete-vote-${v.voter_id}`} onClick={() => del(v.voter_id)}
                className="text-slate-500 hover:text-rose-400" title="Elimina voto">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-slate-300">
            {Object.entries(v.ratings).map(([pid, r]) => (
              <span key={pid}>{playersMap[pid]?.short_name} <b className="font-mono text-emerald-400">{r}</b></span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function VoteDialog({ match, entry, playersMap, isAdmin, onSaved }) {
  const participants = [...match.team1, ...match.team2];
  const [voter, setVoter] = useState(null);
  const [ratings, setRatings] = useState({});
  const [saving, setSaving] = useState(false);

  const pickVoter = (pid) => {
    setVoter(pid);
    setRatings(entry?.votes.find((v) => v.voter_id === pid)?.ratings || {});
  };
  const others = participants.filter((p) => p !== voter);
  const complete = voter && others.every((p) => ratings[p]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/matches/${match.id}/votes`, { voter_id: voter, ratings });
      toast.success("Voti salvati");
      setVoter(null);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md max-h-[90vh] overflow-y-auto" data-testid="vote-dialog">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight">Pagelle · Campo {match.court}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Chi sei?</div>
          <div className="grid grid-cols-2 gap-2">
            {participants.map((pid) => (
              <button key={pid} data-testid={`voter-${pid}`} onClick={() => pickVoter(pid)}
                className={`flex items-center gap-2 p-2 rounded-xl text-sm transition-colors ${
                  voter === pid ? "bg-emerald-500 text-slate-950 font-bold" : "bg-slate-800 text-slate-200 hover:bg-slate-700"
                }`}>
                <PlayerAvatar id={pid} name={playersMap[pid]?.name} size={24} />
                <span className="truncate">{playersMap[pid]?.short_name}</span>
              </button>
            ))}
          </div>
        </div>
        {voter && (
          <div className="space-y-3">
            {others.map((pid) => (
              <RatingRow key={pid} pid={pid} name={playersMap[pid]?.short_name} value={ratings[pid]}
                onChange={(n) => setRatings((r) => ({ ...r, [pid]: n }))} />
            ))}
            <Button onClick={save} disabled={!complete || saving} data-testid="vote-save-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold uppercase text-xs tracking-wide">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Salva voti
            </Button>
          </div>
        )}
        <div className="border-t border-slate-800 pt-3">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Voti dati</div>
          <VotesList match={match} entry={entry} playersMap={playersMap} isAdmin={isAdmin} onDeleted={onSaved} />
        </div>
      </div>
    </DialogContent>
  );
}
